import {getBackendURL, httpResponse, httpResponseError} from "../../fn";
import axios from "axios";
import {RolesEnums, SubjectType} from "../../enums";
import {dealData} from "./deal";

/**
 *
 * @param participantId
 * @return {Promise<{data: *, ok: boolean} | {statusText: *, ok: boolean, message: string, status: *, errors: Array}>}
 */
export const saveParticipantFormValue = async (participantId) => {
  const values = dealData.participants.find((item) => item.participantId === participantId);
  values.representativeId = parseInt(values.representativeId)
  values.representativeDocumentId = parseInt(values.representativeDocumentId)
  values.createAt ? delete values.createAt : void 0;

  const result = axios.put(`${getBackendURL()}/participant/${participantId}/deal/${dealData.dealId}`, values, {
    withCredentials: true,
    headers: {
      'content-type': 'application/json',
    },
  })
    .then(function (response) {
      if (response.status === 200)
        return httpResponse();
    })
    .catch(function (error) {
      return httpResponseError(error);
    });

  return result;
}

/**
 * Новый участник сделки
 * @param type
 * @param role
 * @returns {Promise<unknown>}
 */
export const newParticipant = async (type, role) => {
  const result = await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/participant/deal/${dealData.dealId}`, {type, role}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch(function (error) {
      resolve(httpResponseError(error));
    });
  })

  if (result?.data?.participantId) {
    dealData.participants.push({
      participantId: result.data.participantId,
      subjectId: result.data.subjectId,
      role,
      type,
      documentsRef: []
    })
  }

  return result;
}

/**
 * Новый гарант сделки
 * @returns {Promise<unknown>}
 * @param name
 */
export const newGuarantor = async (name, role) => {
  const result = await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/participant/deal/${dealData.dealId}/guarantor/${name}`, {role}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch(function (error) {
      resolve(httpResponseError(error));
    });
  })

  if (result?.data?.participantId) {
    dealData.participants.push(result.data)
  }

  return result;
}

/**
 * Удалить участника сделки
 * @param participantId
 * @returns {Promise<void>}
 */
export const deleteParticipant = async (participantId) => {
  const result = await new Promise((resolve) => {
    axios.delete(`${getBackendURL()}/participant/${participantId}/deal/${dealData.dealId}`, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    })
      .then(function (response) {
        if (response.status === 200) {
          resolve(httpResponse({}));
        }
      })
      .catch(function (error) {
        resolve(httpResponseError(error));
      });
  });

  return result;
}

/**
 * Добавить существующего субъекта в сделку
 * @param subjectId
 * @param role
 * @returns {Promise<unknown>}
 */
export const addExistSubject = async (subjectId, role) => {
  const result = await new Promise((resolve) => {
    axios.put(`${getBackendURL()}/participant/deal/${dealData.dealId}/subject/${subjectId}`, {role}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch(function (error) {
      resolve(httpResponseError(error));
    });
  })

  if (result?.data?.participantId) {
    dealData.participants.push(result.data)
  }

  return result;
}

/**
 * Получить участников сделки по роли
 * @param role
 * @returns [Participant]
 */
export const getParticipantsWithRole = (role) => {
  const result = dealData.participants.filter((item) => item.role === role);

  return result;
}

/**
 * Добавляем участнику сделки полный адрес в формате json
 * @param personId
 */
export const setAddressDataToSubject = ({participantId, addressData, registrationFullAddress, registrationRegionCode, registrationFiasCode}) => {
  dealData.participants.map((item) => {
    if (
      item.participantId === participantId
    ) {
      item.addressData = addressData;
      item.registrationFullAddress = registrationFullAddress;
      item.registrationFiasCode = registrationFiasCode;
      item.registrationRegionCode = registrationRegionCode;
    }
  })
}

/**
 * Возвращает список документов участника сделки
 */
export const getSubjectListDocuments = ({participantId}) => {
  const doclist = [];

  dealData.participants.map((item) => {
    if (item.participantId === participantId && item.documentsRef?.length) {
      const doc = dealData.documents?.filter((document) => item.documentsRef.includes(document.id));
      if (doc)
        doclist.push(...doc);
    }
  })

  return doclist;
}

/**
 * Возвращает список представителей в сделке в формате для select элемента
 * @returns {{value: string, key: *}[]}
 */
export const getRepresentativeList = () => {
  const result = [];

  for (const participant of dealData.participants) {
    if(participant.role === RolesEnums.REPRESENTATIVE) {
      let value = '';

      if(!participant.lastName || !participant.firstName)
        value = 'Неизвестный'
      else
        value = `${participant?.lastName || ''} ${participant?.firstName || ''} ${participant?.middleName || ''}`

      result.push({
        key: participant.participantId,
        value
      });
    }
  }

  return result;
}

/**
 * Возвращает список субъектов сделки по ИД участника
 * @param participantsId
 * @returns {*[]}
 */
export const getSubjectsWithParticipantId = (participantsId) => {
  if (!participantsId)
    return [];

  if (!Array.isArray(participantsId)) {
    participantsId = [participantsId];
  }
  const result = dealData.participants.filter((item) => participantsId.includes(item.participantId))

  return result;
}

/**
 * Сохраняет данные участника из внутреннего массива участника
 * @param participantId
 * @returns {Promise<{data: *, ok: boolean} | {statusText: *, ok: boolean, message: string, status: *, errors: Array}>}
 */
export const saveParticipantDocumentRef = (participantId) => {
  const participant = dealData.participants.find(item => item.participantId === participantId);
  const values = Object.assign({}, participant);

  values.createAt ? delete values.createAt : void 0;
  values.dealId ? delete values.dealId : void 0;
  values.role ? delete values.role : void 0;

  const result = axios.put(`${getBackendURL()}/participant/${participantId}/deal/${dealData.dealId}/docref`, values, {
    withCredentials: true,
    headers: {
      'content-type': 'application/json',
    },
  })
    .then(function (response) {
      if (response.status === 200)
        return httpResponse();
    })
    .catch(function (error) {
      return httpResponseError(error);
    });

  return result;
}

/**
 * Удаляет ссылку на файл сделки у субъекта сделки
 * @param personId
 */
export const deleteDocRefFromSubject = ({participantId, documentId}) => {
  dealData.participants.map((item) => {
    if (
      item.participantId === participantId
    ) {
      const idx = item.documentsRef.indexOf(documentId);
      item.documentsRef.splice(idx, 1);
    }
  })
}

/**
 * Список участников сделки в формате select элемента
 * Исключаем представителей
 * @returns {{value: string, key: *}[]}
 */
export const getSubjectsFIOList = () => {
  const result = [];
  dealData.participants.map((item) => {
    if(item?.role === RolesEnums.REPRESENTATIVE)
      return;
    if (!Object.values(RolesEnums).includes(item.role))
      return;
    if (item.type === SubjectType.ORGANIZATION)
      result.push({key: item.participantId, value: `${item.name}`});
    if (item.type === SubjectType.PERSON)
      result.push({key: item.participantId, value: `${item?.lastName || ''} ${item?.firstName || ''} ${item?.middleName || ''}`});
  });

  return result;
}

/**
 *
 */
export const getFIWithSubjectId = (subjectId) => {
  const subject = dealData.participants.find((item) => item.subjectId === subjectId)

  if (subject)
    return `${subject?.lastName} ${subject?.firstName} - `;

  return ``;
}


/**
 * Поиск клиента по Фамилии и номеру паспорта
 * @param value
 * @returns {Promise<Array>}
 */
export const findSubject = async (value) => {
  const result = await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/participant/find`, {value}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    })
      .then(function (response) {
        if (response.status === 200) {
          resolve(response.data);
        }
      })
      .catch(function () {
        resolve([])
      });
  });

  return result;
}


/**
 * Поиск клиента по Фамилии и номеру паспорта
 * @param value
 * @returns {Promise<Array>}
 */
export const findSubjectWithInn = async (value) => {
  const result = await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/participant/findrepswithinn`, {value}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    })
      .then(function (response) {
        if (response.status === 200) {
          resolve(response.data);
        }
      })
      .catch(function () {
        resolve([])
      });
  });

  return result;
}

